import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCapability } from '@/contexts/CapabilityContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  LogOut,
  User,
  Settings,
  Activity,
  Cpu,
  Mic,
  ChevronRight,
  Circle,
  Plus,
  LayoutDashboard
} from 'lucide-react';

const DashboardPage = ({ onOpenCapability, onOpenSettings }) => {
  const { user, logout } = useAuth();
  const { capabilities, instances, createInstance, loading } = useCapability();

  const getStateColor = (state) => {
    const colors = {
      idle: 'bg-zinc-500',
      prepared: 'bg-amber-500',
      executing: 'bg-blue-500',
      completed: 'bg-emerald-500',
      failed: 'bg-red-500',
      archived: 'bg-zinc-700'
    };
    return colors[state] || 'bg-zinc-500';
  };

  const getCapabilityIcon = (iconName) => {
    if (iconName === 'mic') return <Mic className="w-5 h-5" />;
    return <Cpu className="w-5 h-5" />;
  };

  const activeInstances = instances.filter(i => i.state !== 'archived');
  const recentInstances = [...activeInstances].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  ).slice(0, 5);

  const handleStartCapability = async (capabilityId) => {
    try {
      const instance = await createInstance(capabilityId);
      onOpenCapability(instance);
    } catch (error) {
      console.error('Failed to create instance:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded bg-zinc-800 border border-zinc-700">
                <LayoutDashboard className="w-4 h-4 text-zinc-400" />
              </div>
              <div>
                <h1 className="text-sm font-medium text-zinc-100">Effortless Metaphor</h1>
                <p className="text-xs text-zinc-500">Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-zinc-800/50 border border-zinc-700">
                <User className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-sm text-zinc-300">{user?.username}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={logout}
                className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Capabilities Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* System Status */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  <CardTitle className="text-sm font-medium text-zinc-100">System Status</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                    <span className="text-zinc-400">Operational</span>
                  </div>
                  <Separator orientation="vertical" className="h-4 bg-zinc-700" />
                  <span className="text-zinc-500">{capabilities.length} Capabilities</span>
                  <span className="text-zinc-500">{activeInstances.length} Active Jobs</span>
                </div>
              </CardContent>
            </Card>

            {/* Available Capabilities */}
            <div>
              <h2 className="text-sm font-medium text-zinc-100 mb-4">Available Capabilities</h2>
              <div className="grid gap-4">
                {capabilities.map((capability) => (
                  <Card 
                    key={capability.id} 
                    className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                    data-testid={`capability-card-${capability.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400">
                            {getCapabilityIcon(capability.icon)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-medium text-zinc-100">{capability.name}</h3>
                              {capability.experimental && (
                                <Badge variant="outline" className="text-xs border-amber-800 text-amber-500">
                                  Experimental
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-zinc-500 mt-0.5">{capability.purpose}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-zinc-600">v{capability.version}</span>
                              <span className="text-xs text-zinc-600">{capability.execution_mode}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleStartCapability(capability.id)}
                          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                          disabled={loading}
                          data-testid={`start-capability-${capability.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Active Jobs */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-100">Active Jobs</CardTitle>
                <CardDescription className="text-zinc-500">Recent capability instances</CardDescription>
              </CardHeader>
              <CardContent>
                {recentInstances.length === 0 ? (
                  <p className="text-sm text-zinc-600 text-center py-4">
                    No active jobs
                  </p>
                ) : (
                  <ScrollArea className="h-[280px]">
                    <div className="space-y-2">
                      {recentInstances.map((instance) => {
                        const capability = capabilities.find(c => c.id === instance.capability_id);
                        return (
                          <button
                            key={instance.id}
                            onClick={() => onOpenCapability(instance)}
                            className="w-full flex items-center justify-between p-2.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 transition-colors text-left"
                            data-testid={`instance-${instance.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getStateColor(instance.state)}`} />
                              <div>
                                <p className="text-xs font-medium text-zinc-200">
                                  {capability?.name || 'Unknown'}
                                </p>
                                <p className="text-xs text-zinc-500">
                                  {instance.id.slice(0, 8)}...
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-100">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                  size="sm"
                  onClick={onOpenSettings}
                  data-testid="settings-button"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
